import { create } from 'zustand';
import type { Tone } from './state.ts';

/* ==========================================================================
   Toasts — the console's transient acknowledgement channel.

   Used for the result of an operator's own action ("stop_capture queued for
   rover-07", "manifest saved"), never for incoming fleet state: a robot going
   into fault is a change on the surface that shows robots, not a popup.

   Failures do not auto-dismiss. An error the operator did not read is an
   error that did not happen.
   ========================================================================== */

export interface Toast {
  id: number;
  tone: Tone;
  message: string;
  /** The mono half — the id, the command, the raw server message. */
  detail?: string | undefined;
  /** ms; 0 or undefined for a toast that stays until dismissed. */
  ttl?: number | undefined;
}

interface ToastState {
  toasts: Toast[];
  push: (t: Omit<Toast, 'id'>) => number;
  dismiss: (id: number) => void;
  clear: () => void;
}

let seq = 0;

export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  push: (t) => {
    seq += 1;
    const id = seq;
    // Four is the most an operator can read before they start stacking up.
    set((s) => ({ toasts: [...s.toasts, { ...t, id }].slice(-4) }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

/** Convenience: an acknowledgement that clears itself. */
export const notifyOk = (message: string, detail?: string) =>
  useToasts.getState().push({ tone: 'good', message, detail, ttl: 4000 });

/** Convenience: a failure that stays until it is dismissed. */
export const notifyError = (message: string, detail?: string) =>
  useToasts.getState().push({ tone: 'error', message, detail });
